import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["className", "wrapperClassName", "innerClassName", "chat", "disabled", "error", "errorText", "full", "hint", "noBorders", "check", "darkMode", "inputClassName", "autoComplete", "autoFocus", "defaultValue", "endAdornment", "adornmentClassNames", "id", "inputProps", "label", "description", "maxLength", "name", "onChange", "onChangeValidation", "onFocus", "onBlur", "onKeyDown", "startAdornment", "type", "value", "data-testid", "data-cy", "textType", "responsive", "wrapperStyles", "rowsMax", "rows", "htmlProps"];
import { forwardRef, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import transition from "../styles/transition";
import CLASSNAMES_BY_TEXT_TYPE from "../Text/responsiveClassnames";
import Adornment from "./Adornment";
import BaseTextArea from "./BaseTextArea";
import { BACKGROUND_COLOR_CLASSNAMES, BASE_INPUT, BORDER_COLOR_CLASSNAMES, HINT_HEX_COLOR, INPUT_TEXT_COLOR_CLASSNAMES } from "./classnames";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var TextArea = function TextArea(_ref, ref) {
  var _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className,
    _ref$wrapperClassName = _ref.wrapperClassName,
    wrapperClassName = _ref$wrapperClassName === void 0 ? '' : _ref$wrapperClassName,
    _ref$innerClassName = _ref.innerClassName,
    innerClassName = _ref$innerClassName === void 0 ? '' : _ref$innerClassName,
    chat = _ref.chat,
    disabled = _ref.disabled,
    error = _ref.error,
    errorText = _ref.errorText,
    full = _ref.full,
    hint = _ref.hint,
    noBorders = _ref.noBorders,
    check = _ref.check,
    darkMode = _ref.darkMode,
    inputClassName = _ref.inputClassName,
    autoComplete = _ref.autoComplete,
    autoFocus = _ref.autoFocus,
    defaultValue = _ref.defaultValue,
    endAdornment = _ref.endAdornment,
    adornmentClassNames = _ref.adornmentClassNames,
    id = _ref.id,
    inputProps = _ref.inputProps,
    label = _ref.label,
    description = _ref.description,
    maxLength = _ref.maxLength,
    name = _ref.name,
    onChangeProp = _ref.onChange,
    onChangeValidation = _ref.onChangeValidation,
    onFocusProp = _ref.onFocus,
    onBlurProp = _ref.onBlur,
    onKeyDown = _ref.onKeyDown,
    startAdornment = _ref.startAdornment,
    type = _ref.type,
    value = _ref.value,
    dataTestId = _ref['data-testid'],
    dataCy = _ref['data-cy'],
    _ref$textType = _ref.textType,
    textType = _ref$textType === void 0 ? 'body3' : _ref$textType,
    _ref$responsive = _ref.responsive,
    responsive = _ref$responsive === void 0 ? 'sm' : _ref$responsive,
    wrapperStyles = _ref.wrapperStyles,
    rowsMax = _ref.rowsMax,
    _ref$rows = _ref.rows,
    rows = _ref$rows === void 0 ? 1 : _ref$rows,
    htmlProps = _ref.htmlProps,
    textAreaProps = _objectWithoutProperties(_ref, _excluded);
  var textAreaElement = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    focused = _useState2[0],
    setFocused = _useState2[1];
  var _useState3 = useState(rows > 1),
    _useState4 = _slicedToArray(_useState3, 2),
    isMultirow = _useState4[0],
    setIsMultirow = _useState4[1];
  useEffect(function () {
    if (autoFocus && textAreaElement.current) {
      textAreaElement.current.focus();
    }
  }, [autoFocus]);
  var onChange = function onChange(e) {
    if (typeof onChangeProp === 'function' && (typeof onChangeValidation !== 'function' || onChangeValidation(e.target.value))) {
      onChangeProp(e);
    }
  };
  var onFocus = function onFocus(e) {
    setFocused(true);
    if (typeof onFocusProp === 'function') {
      onFocusProp(e);
    }
  };
  var onBlur = function onBlur(e) {
    setFocused(false);
    if (typeof onBlurProp === 'function') {
      onBlurProp(e);
    }
  };
  var assignRef = function assignRef(i) {
    if (typeof ref === 'function') {
      ref(i);
    } else if (ref) {
      ref.current = i;
    }
    textAreaElement.current = i;
  };
  var onMultirowChange = function onMultirowChange(newIsMultirow) {
    setIsMultirow(newIsMultirow);
  };
  var fontClassNames = CLASSNAMES_BY_TEXT_TYPE(responsive)[textType];
  var backgroundState = darkMode ? 'dark' : 'light';
  var borderState = darkMode ? 'dark' : 'light';
  var inputState = darkMode ? 'dark' : 'light';
  if (focused && !chat) {
    borderState = darkMode ? 'dark-focused' : 'dark-focused';
  }
  if (error || errorText) {
    inputState = darkMode ? 'dark-error' : 'light-error';
    borderState = 'error';
  }
  if (disabled) {
    backgroundState = darkMode ? 'dark-disabled' : 'light-disabled';
    inputState = darkMode ? 'dark-disabled' : 'light-disabled';
    borderState = 'disabled';
  }
  var inputPaddingClassNames = '';
  if (!noBorders) {
    inputPaddingClassNames = chat ? 'py-8' : 'py-16';
    if (startAdornment || check || endAdornment) {
      inputPaddingClassNames = cn(inputPaddingClassNames, startAdornment ? 'pl-8' : 'pl-16', endAdornment || check ? 'pr-8' : 'pr-16');
    } else {
      inputPaddingClassNames = cn(inputPaddingClassNames, 'px-16');
    }
  }
  var adornmentProps = {
    startAdornment: startAdornment,
    endAdornment: endAdornment,
    adornmentClassNames: adornmentClassNames,
    chat: chat,
    multirow: isMultirow
  };
  return /*#__PURE__*/_jsxs("label", _objectSpread(_objectSpread(_objectSpread({
    className: cn('inline-flex flex-col', {
      'w-full': full
    }, className)
  }, textAreaProps), htmlProps), {}, {
    children: [label && /*#__PURE__*/_jsx("span", {
      className: cn('font-subtitle3 mb-8', darkMode ? 'text-white' : 'text-base-main'),
      children: label
    }), description && /*#__PURE__*/_jsx("p", {
      className: cn('font-body4 mb-8', darkMode ? 'text-base-lighter' : 'text-gray-dark'),
      children: description
    }), /*#__PURE__*/_jsx("div", {
      className: cn('inline-flex', _defineProperty({}, "rounded-5 border border-solid ".concat(BORDER_COLOR_CLASSNAMES[borderState], " ").concat(BACKGROUND_COLOR_CLASSNAMES[backgroundState]), !noBorders), wrapperClassName),
      style: _objectSpread({
        transition: transition(['background-color', 'border'])
      }, wrapperStyles),
      children: /*#__PURE__*/_jsxs("div", {
        className: cn('flex w-full items-end', innerClassName),
        children: [startAdornment && /*#__PURE__*/_jsx(Adornment, _objectSpread({
          type: "start"
        }, adornmentProps)), /*#__PURE__*/_jsx(BaseTextArea, _objectSpread({
          "data-testid": dataTestId,
          "data-cy": dataCy,
          className: cn('crehui-textarea', darkMode ? 'crehui-scrollable-dark' : 'crehui-scrollable', BASE_INPUT, INPUT_TEXT_COLOR_CLASSNAMES[inputState], fontClassNames, inputPaddingClassNames, inputClassName),
          areaRef: assignRef,
          hintColor: HINT_HEX_COLOR[backgroundState],
          placeholder: hint,
          autoComplete: autoComplete,
          defaultValue: defaultValue ? defaultValue.toString() : undefined,
          disabled: disabled,
          id: id,
          name: name,
          type: type,
          value: value,
          onKeyDown: onKeyDown,
          maxLength: maxLength || undefined,
          onChange: onChange,
          onFocus: onFocus,
          onBlur: onBlur,
          rowsMin: rows,
          rowsMax: rowsMax,
          onMultirowChange: onMultirowChange
        }, inputProps)), check && /*#__PURE__*/_jsx(Adornment, _objectSpread({
          type: "check"
        }, adornmentProps)), endAdornment && /*#__PURE__*/_jsx(Adornment, _objectSpread({
          type: "end"
        }, adornmentProps))]
      })
    }), errorText && /*#__PURE__*/_jsx("span", {
      className: cn('font-caption mt-8', disabled ? 'text-gray-dark' : 'text-red-main'),
      role: "alert",
      children: errorText
    })]
  }));
};
export default /*#__PURE__*/forwardRef(TextArea);